/*
 * im-events-pixel.js — first-party touchpoint pixel for interiormoderna.com.
 *
 * Phase 2 of the Deep Customer & Product Intelligence program
 * (plan: ~/.claude/plans/i-want-to-build-elegant-boot.md).
 *
 * What it does:
 *   - Manages a persistent anon_id (localStorage, 1yr) shared with the cart
 *     insights pixel and the Clarity custom-tag, so one visitor = one ID
 *     across cart events, page views, and Clarity replays.
 *   - Rotates a session_id every 30 min of inactivity.
 *   - Fires "page_view" on every navigation, "scroll_depth" at 25/50/75/100%,
 *     and "identify" whenever a customer email becomes available via the
 *     window.imPixelContext handshake seeded by im-events-pixel.liquid.
 *   - Batches events in-memory, flushes every 5 s, on visibilitychange
 *     (hidden), and on pagehide (sendBeacon) so nothing is lost when a tab
 *     closes.
 *   - Honors Do Not Track + Global Privacy Control — if signalled, the
 *     pixel no-ops before any network IO.
 *
 * Safety:
 *   - Every public entry point is wrapped in try/catch. A broken pixel must
 *     never break the storefront.
 *   - If a fetch fails, events stay in the queue up to MAX_QUEUE. Oldest
 *     drop first so the queue can't grow unbounded.
 *   - localStorage failures (Safari private mode) degrade gracefully —
 *     anon_id becomes per-session instead of persistent.
 */
(function () {
  "use strict";

  // ── Privacy gate ────────────────────────────────────────────────────────
  try {
    var dnt =
      navigator.doNotTrack === "1" ||
      window.doNotTrack === "1" ||
      navigator.msDoNotTrack === "1";
    var gpc = navigator.globalPrivacyControl === true;
    if (dnt || gpc) return;
  } catch (_) {
    /* feature-detect failed — don't block analytics on that */
  }

  // ── Config ──────────────────────────────────────────────────────────────
  // Default to the Railway-hosted ops dashboard, same pattern as
  // im-cart-tracker.liquid. The snippet (im-events-pixel.liquid) can
  // override via window.IM_EVENTS_ENDPOINT for staging/dev.
  var ENDPOINT =
    "https://im-ops-dashboard-production.up.railway.app/api/storefront/web-events/track";
  if (typeof window.IM_EVENTS_ENDPOINT === "string" && window.IM_EVENTS_ENDPOINT) {
    ENDPOINT = window.IM_EVENTS_ENDPOINT;
  }

  var ANON_KEY = "im_anon_id";
  var SESSION_KEY = "im_session_id";
  var SESSION_LAST_KEY = "im_session_last_at";
  var SESSION_IDLE_MS = 30 * 60 * 1000;
  var FLUSH_INTERVAL_MS = 5000;
  var MAX_QUEUE = 100;
  var MAX_RETRY = 3;
  var SCROLL_THRESHOLDS = [25, 50, 75, 100];

  // ── Identity helpers ────────────────────────────────────────────────────
  function newId() {
    try {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
      }
    } catch (_) {}
    // Fallback: generate a valid UUID v4 so the DB UUID column accepts it.
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function lsGet(k) {
    try {
      return window.localStorage.getItem(k);
    } catch (_) {
      return null;
    }
  }
  function lsSet(k, v) {
    try {
      window.localStorage.setItem(k, v);
    } catch (_) {}
  }

  function getAnonId() {
    var aid = lsGet(ANON_KEY);
    if (!aid) {
      aid = newId();
      lsSet(ANON_KEY, aid);
    }
    return aid;
  }

  function getSessionId() {
    var now = Date.now();
    var last = parseInt(lsGet(SESSION_LAST_KEY) || "0", 10);
    var sid = lsGet(SESSION_KEY);
    if (!sid || !last || now - last > SESSION_IDLE_MS) {
      sid = newId();
      lsSet(SESSION_KEY, sid);
    }
    lsSet(SESSION_LAST_KEY, String(now));
    return sid;
  }

  // ── Context from Liquid (seeded by im-events-pixel.liquid snippet) ──────
  var ctx = window.imPixelContext || {};
  var ANON_ID = getAnonId();

  function deviceType() {
    var ua = (navigator.userAgent || "").toLowerCase();
    if (/ipad|tablet|playbook|silk/.test(ua)) return "tablet";
    if (/mobi|iphone|ipod|android.*mobile/.test(ua)) return "mobile";
    return "desktop";
  }

  function getUtms() {
    var out = {};
    try {
      var sp = new URLSearchParams(window.location.search);
      ["source", "medium", "campaign", "term", "content"].forEach(function (k) {
        var v = sp.get("utm_" + k);
        if (v) out["utm_" + k] = v;
      });
    } catch (_) {}
    return out;
  }

  // ── Event queue ─────────────────────────────────────────────────────────
  var queue = [];
  var flushTimer = null;

  function baseEvent(type, extra) {
    var now = new Date().toISOString();
    var evt = {
      event_type: type,
      occurred_at: now,
      session_id: getSessionId(),
      anon_id: ANON_ID,
      customer_email: ctx.customer_email || null,
      shopify_customer_id: ctx.shopify_customer_id || null,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title || null,
      referrer: document.referrer || null,
      sku: ctx.sku || null,
      collection_handle: ctx.collection_handle || null,
      viewport_w: window.innerWidth || null,
      viewport_h: window.innerHeight || null,
      device_type: deviceType(),
      user_agent: navigator.userAgent || null
    };
    var utms = getUtms();
    for (var k in utms) {
      if (Object.prototype.hasOwnProperty.call(utms, k)) evt[k] = utms[k];
    }
    if (extra && typeof extra === "object") {
      for (var k2 in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k2)) evt[k2] = extra[k2];
      }
    }
    return evt;
  }

  function enqueue(evt) {
    queue.push(evt);
    if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
    if (!flushTimer) {
      flushTimer = window.setTimeout(flush, FLUSH_INTERVAL_MS);
    }
  }

  function flush() {
    if (flushTimer) {
      window.clearTimeout(flushTimer);
      flushTimer = null;
    }
    if (!queue.length) return;
    var batch = queue.slice();
    queue = [];
    postBatch(batch, 0);
  }

  function postBatch(batch, attempt) {
    var body = JSON.stringify({ events: batch });
    try {
      fetch(ENDPOINT, {
        method: "POST",
        credentials: "omit",
        mode: "cors",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: body
      }).then(
        function (res) {
          if (!res.ok && attempt < MAX_RETRY) {
            window.setTimeout(function () {
              postBatch(batch, attempt + 1);
            }, 1000 * (attempt + 1));
          }
        },
        function () {
          if (attempt < MAX_RETRY) {
            window.setTimeout(function () {
              postBatch(batch, attempt + 1);
            }, 1000 * (attempt + 1));
          }
        }
      );
    } catch (_) {
      /* network broken — let the batch drop */
    }
  }

  function flushBeacon() {
    if (!queue.length) return;
    var body = JSON.stringify({ events: queue });
    queue = [];
    try {
      if (navigator.sendBeacon) {
        var blob = new Blob([body], { type: "application/json" });
        navigator.sendBeacon(ENDPOINT, blob);
        return;
      }
    } catch (_) {}
    postBatch(JSON.parse(body).events, 0);
  }

  // ── Built-in event sources ──────────────────────────────────────────────
  function fireInitial() {
    // page_view
    enqueue(
      baseEvent("page_view", {
        properties: {
          page_type: ctx.page_type || null,
          template: ctx.template || null
        }
      })
    );

    // identify (if customer known)
    if (ctx.customer_email) {
      enqueue(
        baseEvent("identify", {
          properties: {
            source: "liquid",
            customer_name: ctx.customer_name || null
          }
        })
      );
    }

    // product_view (auto-fires on PDP)
    if (ctx.page_type === "product" && ctx.sku) {
      enqueue(baseEvent("product_view", {}));
    }

    // collection_view (auto-fires on collection)
    if (ctx.page_type === "collection" && ctx.collection_handle) {
      enqueue(baseEvent("collection_view", {}));
    }
  }

  var scrollFired = {};
  function onScroll() {
    try {
      var doc = document.documentElement;
      var scrollTop = window.pageYOffset || doc.scrollTop || 0;
      var max = Math.max(doc.scrollHeight - window.innerHeight, 1);
      var pct = Math.min(100, Math.round((scrollTop / max) * 100));
      for (var i = 0; i < SCROLL_THRESHOLDS.length; i++) {
        var t = SCROLL_THRESHOLDS[i];
        if (pct >= t && !scrollFired[t]) {
          scrollFired[t] = true;
          enqueue(baseEvent("scroll_depth", { scroll_pct: t }));
        }
      }
    } catch (_) {}
  }

  // ── Public API (for theme code to fire custom events) ───────────────────
  window.imEvents = {
    track: function (type, extra) {
      try {
        if (!type) return;
        enqueue(baseEvent(String(type), extra || {}));
      } catch (_) {}
    },
    identify: function (email, extra) {
      try {
        if (!email) return;
        ctx.customer_email = email;
        // Wrap extra in properties so the server sanitizer captures it in JSONB.
        enqueue(baseEvent("identify", { properties: extra || { source: "api" } }));
      } catch (_) {}
    },
    flush: flush
  };

  // ── Automatic identify on email capture ─────────────────────────────────
  //
  // WHY THIS IS DELEGATED AND NOT SIX CALL SITES (2026-08-12).
  // `imEvents.identify()` existed from day one and almost nothing called it —
  // only the wishlist. Measured: 0 of 200 recent product_view rows carry a
  // profile_id while 200 of 200 carry a browser_id, so ~79% of the events our
  // lifecycle flows trigger on belong to visitors we cannot name, and Browse
  // Abandonment reaches almost nobody.
  //
  // The theme captures an email in at least six places (register, contact,
  // trade, commission, newsletter popup, plus the Klaviyo-embedded footer
  // form). Adding a call to each one fixes today and rots tomorrow: the next
  // form anybody builds will forget it, silently, and nothing will fail. One
  // delegated listener covers every form that exists, every form added later,
  // and third-party embeds that render into the page.
  //
  // SUBMIT ONLY, deliberately. Firing on blur/change would capture the address
  // of someone who typed it and changed their mind — that is a policy decision
  // about people who did not choose to give it to us, not a technical one.
  // Submitting a form is an intentional act.
  var identified = {};
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function emailFrom(root) {
    if (!root || !root.querySelectorAll) return null;
    var fields = root.querySelectorAll(
      'input[type="email"], input[name="email"], input[name="contact[email]"], input[name="customer[email]"]'
    );
    for (var i = 0; i < fields.length; i++) {
      var v = (fields[i].value || "").trim().toLowerCase();
      if (EMAIL_RE.test(v)) return v;
    }
    return null;
  }

  function onAnyFormSubmit(evt) {
    try {
      var email = emailFrom(evt && evt.target);
      if (!email || identified[email]) return;
      // Already known from Liquid — fireInitial() has covered this visitor.
      if ((ctx.customer_email || "").toLowerCase() === email) return;
      identified[email] = true;
      window.imEvents.identify(email, { source: "form_submit" });
      // The visitor is mid-navigation; a queued event would die with the page.
      flush();
    } catch (_) {}
  }

  // Capture phase: third-party form scripts (Klaviyo's embed) call
  // preventDefault + stopPropagation in their own handler, which would keep a
  // bubble-phase listener from ever seeing the submit.
  document.addEventListener("submit", onAnyFormSubmit, true);

  // ── Wire up ─────────────────────────────────────────────────────────────
  try {
    fireInitial();

    window.addEventListener("scroll", onScroll, { passive: true });

    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "hidden") flush();
    });

    window.addEventListener("pagehide", flushBeacon);
    window.addEventListener("beforeunload", flushBeacon);
  } catch (_) {
    /* wiring failed — the pixel is a no-op; storefront continues unharmed */
  }
})();
